import { useId, useMemo, useState } from "react";
import { Button, IconButton, SvgIcon, Tooltip } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import {
    ChatPlatform,
    CreateChatBotRequest
} from "../../../common/interfaces/admin/chat-bot.interface";

interface Props {
    open: boolean;
    knowledgeBaseUid: string;
    onCancel: () => void;
    onSubmit: (data: CreateChatBotRequest) => Promise<void>;
}

const initialFormState = {
    name: "",
    platform: ChatPlatform.Cliq as ChatPlatform,
    signingSecret: "",
    microsoftAppId: "",
    microsoftAppPassword: "",
    tenantId: "",
    googleProjectNumber: "",
    authenticationAudience: "",
    googleServiceAccountJson: "",
    slackSigningSecret: "",
    slackBotToken: ""
};

const CreateChatBotModal = ({ open, knowledgeBaseUid, onCancel, onSubmit }: Props) => {
    const [form, setForm] = useState(initialFormState);
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string>("");
    // Whether an invalid submit has been attempted — gates the announced
    // (role=alert) validation summary so it doesn't fire before they try (3.3.1).
    const [showErrors, setShowErrors] = useState(false);
    const [showKey, setShowKey] = useState(false);
    // Base id for associating <label htmlFor> with each field's control (WCAG 1.3.1/3.3.2).
    const fieldId = useId();

    const isTeams = form.platform === ChatPlatform.Teams;
    const isGoogleChat = form.platform === ChatPlatform.GoogleChat;
    const isSlack = form.platform === ChatPlatform.Slack;

    const generateApiKey = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let key = "";
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        for (const byte of array) key += chars[byte % chars.length];
        setForm({ ...form, signingSecret: key });
        setShowKey(true);
    };

    const copyApiKey = () => navigator.clipboard.writeText(form.signingSecret);

    const validation = useMemo(() => {
        const issues: string[] = [];
        if (!form.name.trim()) issues.push("Name is required");
        if (isTeams) {
            if (!form.microsoftAppId.trim()) issues.push("Microsoft App ID is required");
            if (!form.microsoftAppPassword.trim()) issues.push("App Password is required");
            if (!form.tenantId.trim()) issues.push("Tenant ID is required");
        } else if (isGoogleChat) {
            if (!form.googleProjectNumber.trim()) issues.push("Project Number is required");
            if (!form.authenticationAudience.trim())
                issues.push("Authentication Audience is required");
            if (!form.googleServiceAccountJson.trim())
                issues.push("Service Account JSON is required");
            else {
                try {
                    const parsed = JSON.parse(form.googleServiceAccountJson);
                    if (!parsed.client_email || !parsed.private_key) {
                        issues.push(
                            "Service Account JSON must include client_email and private_key"
                        );
                    }
                } catch {
                    issues.push("Service Account JSON is not valid JSON");
                }
            }
        } else if (isSlack) {
            if (!form.slackSigningSecret.trim()) issues.push("Signing Secret is required");
            if (!form.slackBotToken.trim()) issues.push("Bot Token is required");
        } else {
            if (!form.signingSecret.trim()) issues.push("API key is required");
        }
        return { isValid: issues.length === 0, issues };
    }, [form, isTeams, isGoogleChat, isSlack]);

    const resetAndClose = () => {
        setForm(initialFormState);
        setServerError("");
        setShowErrors(false);
        setShowKey(false);
        onCancel();
    };

    const handleSubmit = async () => {
        if (!validation.isValid) {
            setShowErrors(true);
            return;
        }
        setSubmitting(true);
        setServerError("");
        try {
            let credentials: Record<string, string>;
            if (isTeams) {
                credentials = {
                    microsoftAppId: form.microsoftAppId.trim(),
                    microsoftAppPassword: form.microsoftAppPassword.trim(),
                    tenantId: form.tenantId.trim()
                };
            } else if (isGoogleChat) {
                credentials = {
                    googleProjectNumber: form.googleProjectNumber.trim(),
                    authenticationAudience: form.authenticationAudience.trim(),
                    serviceAccountJson: form.googleServiceAccountJson.trim()
                };
            } else if (isSlack) {
                credentials = {
                    signingSecret: form.slackSigningSecret.trim(),
                    botToken: form.slackBotToken.trim()
                };
            } else {
                credentials = { signingSecret: form.signingSecret.trim() };
            }

            await onSubmit({
                name: form.name.trim(),
                platform: form.platform,
                platformBotId: form.name.trim(),
                knowledgeBaseUid,
                credentials
            });
            setForm(initialFormState);
        } catch (err: any) {
            setServerError(err?.response?.data?.message || "Failed to create bot");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalTemplate
            open={open}
            width={500}
            handleClose={resetAndClose}
            ariaLabel="Create Chat Bot"
        >
            <div className="chat-bot-form">
                <h2 className="chat-bot-form__title">Create Chat Bot</h2>

                <div className="chat-bot-form__field">
                    <label htmlFor={`${fieldId}-name`}>Name</label>
                    <input
                        id={`${fieldId}-name`}
                        type="text"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        placeholder={isTeams ? "Teams Support Bot" : "Support Bot"}
                    />
                </div>

                <div className="chat-bot-form__field">
                    <label htmlFor={`${fieldId}-platform`}>Platform</label>
                    <select
                        id={`${fieldId}-platform`}
                        value={form.platform}
                        onChange={e =>
                            setForm({
                                ...initialFormState,
                                name: form.name,
                                platform: e.target.value as ChatPlatform
                            })
                        }
                    >
                        <option value={ChatPlatform.Cliq}>Zoho Cliq</option>
                        <option value={ChatPlatform.Teams}>Microsoft Teams</option>
                        <option value={ChatPlatform.GoogleChat}>Google Chat</option>
                        <option value={ChatPlatform.Slack}>Slack</option>
                    </select>
                </div>

                {isTeams ? (
                    <>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-msAppId`}>Microsoft App ID</label>
                            <input
                                id={`${fieldId}-msAppId`}
                                type="text"
                                value={form.microsoftAppId}
                                onChange={e => setForm({ ...form, microsoftAppId: e.target.value })}
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                aria-describedby={`${fieldId}-msAppId-help`}
                            />
                            <span
                                id={`${fieldId}-msAppId-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Azure Portal &rarr; App registrations &rarr; Application (client) ID
                            </span>
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-msAppPassword`}>
                                Microsoft App Password
                            </label>
                            <input
                                id={`${fieldId}-msAppPassword`}
                                type="password"
                                value={form.microsoftAppPassword}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        microsoftAppPassword: e.target.value
                                    })
                                }
                                autoComplete="off"
                                aria-describedby={`${fieldId}-msAppPassword-help`}
                            />
                            <span
                                id={`${fieldId}-msAppPassword-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Azure Portal &rarr; Certificates &amp; secrets &rarr; Client secret
                                value
                            </span>
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-tenantId`}>Tenant ID</label>
                            <input
                                id={`${fieldId}-tenantId`}
                                type="text"
                                value={form.tenantId}
                                onChange={e => setForm({ ...form, tenantId: e.target.value })}
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                aria-describedby={`${fieldId}-tenantId-help`}
                            />
                            <span
                                id={`${fieldId}-tenantId-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Azure Portal &rarr; Directory (tenant) ID
                            </span>
                        </div>
                    </>
                ) : isGoogleChat ? (
                    <>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-gcpProjectNumber`}>
                                Google Cloud Project Number
                            </label>
                            <input
                                id={`${fieldId}-gcpProjectNumber`}
                                type="text"
                                value={form.googleProjectNumber}
                                onChange={e =>
                                    setForm({ ...form, googleProjectNumber: e.target.value })
                                }
                                placeholder="123456789012"
                                aria-describedby={`${fieldId}-gcpProjectNumber-help`}
                            />
                            <span
                                id={`${fieldId}-gcpProjectNumber-help`}
                                className="chat-bot-form__field-helper"
                            >
                                GCP Console &rarr; Project settings &rarr; Project number
                            </span>
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-authAudience`}>
                                Authentication Audience
                            </label>
                            <input
                                id={`${fieldId}-authAudience`}
                                type="text"
                                value={form.authenticationAudience}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        authenticationAudience: e.target.value
                                    })
                                }
                                placeholder="https://your-api.com/api/webhooks/googleChat/bot_..."
                                aria-describedby={`${fieldId}-authAudience-help`}
                            />
                            <span
                                id={`${fieldId}-authAudience-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Must match the Authentication Audience setting in GCP Chat API
                                configuration.
                            </span>
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-serviceAccountJson`}>
                                Service Account JSON Key
                            </label>
                            <textarea
                                id={`${fieldId}-serviceAccountJson`}
                                rows={6}
                                value={form.googleServiceAccountJson}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        googleServiceAccountJson: e.target.value
                                    })
                                }
                                placeholder='{"type":"service_account","project_id":"...","client_email":"...","private_key":"..."}'
                                aria-describedby={`${fieldId}-serviceAccountJson-help`}
                            />
                            <span
                                id={`${fieldId}-serviceAccountJson-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Paste the full JSON key from GCP &rarr; IAM &amp; Admin &rarr;
                                Service Accounts. The service account must have the <b>Chat Bot</b>{" "}
                                role. Used to post replies asynchronously.
                            </span>
                        </div>
                    </>
                ) : isSlack ? (
                    <>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-signingSecret`}>Signing Secret</label>
                            <input
                                id={`${fieldId}-signingSecret`}
                                type="password"
                                value={form.slackSigningSecret}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        slackSigningSecret: e.target.value
                                    })
                                }
                                autoComplete="off"
                                aria-describedby={`${fieldId}-signingSecret-help`}
                            />
                            <span
                                id={`${fieldId}-signingSecret-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Slack App &rarr; Basic Information &rarr; Signing Secret
                            </span>
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-botUserToken`}>Bot User OAuth Token</label>
                            <input
                                id={`${fieldId}-botUserToken`}
                                type="password"
                                value={form.slackBotToken}
                                onChange={e => setForm({ ...form, slackBotToken: e.target.value })}
                                autoComplete="off"
                                placeholder="xoxb-..."
                                aria-describedby={`${fieldId}-botUserToken-help`}
                            />
                            <span
                                id={`${fieldId}-botUserToken-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Slack App &rarr; OAuth &amp; Permissions &rarr; Bot User OAuth Token
                            </span>
                        </div>
                    </>
                ) : (
                    <div className="chat-bot-form__field">
                        <label htmlFor={`${fieldId}-apiKey`}>API Key</label>
                        <div className="chat-bot-form__field-with-actions">
                            <input
                                id={`${fieldId}-apiKey`}
                                type={showKey ? "text" : "password"}
                                value={form.signingSecret}
                                onChange={e => setForm({ ...form, signingSecret: e.target.value })}
                                autoComplete="off"
                                aria-describedby={`${fieldId}-apiKey-help`}
                            />
                            <IconButton
                                className="chat-bot-form__field-action"
                                size="small"
                                onClick={() => setShowKey(!showKey)}
                                aria-label={showKey ? "Hide API key" : "Show API key"}
                                aria-pressed={showKey}
                                title={showKey ? "Hide" : "Show"}
                            >
                                <SvgIcon
                                    component={showKey ? VisibilityOffIcon : VisibilityIcon}
                                    fontSize="small"
                                />
                            </IconButton>
                            <IconButton
                                className="chat-bot-form__field-action"
                                size="small"
                                onClick={copyApiKey}
                                disabled={!form.signingSecret}
                                aria-label="Copy API key"
                                title="Copy API key"
                            >
                                <SvgIcon component={ContentCopyIcon} fontSize="small" />
                            </IconButton>
                            <IconButton
                                className="chat-bot-form__field-action"
                                size="small"
                                onClick={generateApiKey}
                                aria-label="Generate random API key"
                                title="Generate random API key"
                            >
                                <SvgIcon component={AutorenewIcon} fontSize="small" />
                            </IconButton>
                        </div>
                        <span id={`${fieldId}-apiKey-help`} className="chat-bot-form__field-helper">
                            Generate a key, then paste it into your Cliq handler as the x-api-key
                            header value.
                        </span>
                    </div>
                )}

                {showErrors && !validation.isValid && (
                    <div className="chat-bot-form__error" role="alert">
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {validation.issues.map((issue, i) => (
                                <li key={i}>{issue}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {serverError && (
                    <div className="chat-bot-form__error" role="alert">
                        {serverError}
                    </div>
                )}

                <div className="chat-bot-form__actions">
                    <Button
                        className="chat-bot-form__cancel"
                        variant="text"
                        onClick={resetAndClose}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Tooltip
                        title={
                            !validation.isValid ? (
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {validation.issues.map((issue, i) => (
                                        <li key={i}>{issue}</li>
                                    ))}
                                </ul>
                            ) : (
                                ""
                            )
                        }
                        disableHoverListener={validation.isValid}
                    >
                        <span>
                            <Button
                                className="chat-bot-form__submit"
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? "Creating..." : "Create"}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default CreateChatBotModal;
